import type {
  IErrorApiResponse,
  ISuccessApiResponse,
  IValidationError,
} from '@wyz/types';
import {
  DateUtils,
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  restrictApi,
  serialize,
  useMutation,
  useQuery,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { CommonErrorCodesEnumType } from '~/common/types';
import { ContainerOrderQueryKeys } from '~/container-order/api';
import {
  IContainerPricingPolicy,
  IContainerPricingPolicyAssignmentPayload,
  IContainerPricingPolicyDetail,
  IContainerPricingPolicyPayload,
  IContainerPricingPolicyValidationError,
  IContainerType,
  IUpdateContainerPricingPolicyPayload,
} from '~/container-order/types';
import { isNil } from 'lodash-es';

type Options = {
  create?: UseMutationOptions<
    ISuccessApiResponse<Array<IContainerPricingPolicy>>,
    IErrorApiResponse<
      IContainerPricingPolicyValidationError,
      CommonErrorCodesEnumType
    >,
    IContainerPricingPolicyPayload
  >;
  get?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IContainerPricingPolicyDetail>,
      IErrorApiResponse
    >
  > & {
    args: {
      policyGroupId: number;
    };
  };
  update?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse<
      IContainerPricingPolicyValidationError,
      CommonErrorCodesEnumType,
      Record<string, unknown>
    >,
    {
      policyGroupId: number;
      payload: IUpdateContainerPricingPolicyPayload;
    }
  >;
  assign?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >,
    {
      supplierId: number;
      body: IContainerPricingPolicyAssignmentPayload;
    }
  >;
  listBySupplier?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<IContainerPricingPolicy>>,
      IErrorApiResponse
    >
  > & {
    args: {
      supplierId: number;
      containerType: IContainerType;
    };
  };
};

export const useManageContainerPricingPolicies = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi, pricingPoliciesDomainBaseUrl } = useApi();
  const { tenantCode } = useConfig();

  const createMutation = useMutation({
    ...options.create,
    mutationFn: (variables) => {
      const { file, ...payload } = variables;
      const formData = new FormData();
      formData.append('file', file, file.name);
      Object.entries(payload).forEach(([key, value]) => {
        if (!isNil(value)) {
          formData.append(
            key,
            value instanceof Date ? DateUtils.format(value) : String(value),
          );
        }
      });
      return callCoreApi<ISuccessApiResponse<Array<IContainerPricingPolicy>>>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/suppliers/${payload.supplier}/container-pricing-policies/import`,
        {
          method: 'POST',
          body: formData,
          asJson: false,
        },
      );
    },
  });

  const listBySupplierQuery = useQuery({
    refetchOnWindowFocus: false,
    ...options.listBySupplier,
    enabled:
      Boolean(options.listBySupplier?.enabled) &&
      Boolean(options.listBySupplier?.args?.supplierId),
    queryKey: ContainerOrderQueryKeys.containerPricingPolicies.bySupplier(
      options.listBySupplier?.args,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<IContainerPricingPolicy>>>(
        `/pricing-policies/api/tenants/${tenantCode}/suppliers/${String(options.listBySupplier?.args?.supplierId)}/container-pricing-policies?${serialize(
          {
            containerType: options.listBySupplier?.args?.containerType,
          },
        )}`,
      ),
  });

  const assignMutation = useMutation({
    ...options.assign,
    keysToInvalidateOnSuccess: (variables) => [
      ContainerOrderQueryKeys.containerPricingPolicies.base(),
      ContainerOrderQueryKeys.containerPricingPolicies.bySupplier(
        variables.supplierId,
      ),
    ],
    mutationFn: (variables) => {
      return callCoreApi<ISuccessApiResponse>(
        `/pricing-policies/api/tenants/${tenantCode}/suppliers/${variables.supplierId}/container-pricing-policies/assign`,
        {
          method: 'POST',
          body: JSON.stringify(variables.body),
        },
      );
    },
  });

  const getQuery = useQuery({
    refetchOnWindowFocus: false,
    ...options.get,
    enabled:
      Boolean(options.get?.enabled) &&
      Boolean(options.get?.args?.policyGroupId),
    queryKey: ContainerOrderQueryKeys.containerPricingPolicies.detail(
      options.get?.args?.policyGroupId,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IContainerPricingPolicyDetail>>(
        `/pricing-policies/api/tenants/${tenantCode}/container-pricing-policies/${options.get?.args?.policyGroupId as number}`,
      ),
  });

  const updateMutation = useMutation({
    ...options.update,
    keysToInvalidateOnSuccess: (variables) => [
      ContainerOrderQueryKeys.containerPricingPolicies.base(),
      ContainerOrderQueryKeys.containerPricingPolicies.detail(
        variables.policyGroupId,
      ),
    ],
    mutationFn: (variables) => {
      return callCoreApi<ISuccessApiResponse>(
        `/pricing-policies/api/tenants/${tenantCode}/container-pricing-policies/${variables.policyGroupId}`,
        {
          method: 'PUT',
          body: JSON.stringify(variables.payload),
        },
      );
    },
  });

  return restrictApi(options, {
    create: createMutation,
    get: getQuery,
    update: updateMutation,
    listBySupplier: listBySupplierQuery,
    assign: assignMutation,
  });
};
